import PropTypes from 'prop-types';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';

import useUserContext from '../hooks/use-user-context';
import { applyPricingFormat, getPhoneNumbers } from '../utilities/localization';
import {
  createOptionMap,
  mergeQueryParams,
  getBrowserLanguage,
  isBrowser,
  isVisitorMSIE
} from '../utilities/misc';

const VariableText = ({ type, name, render }) => {
  const data = useStaticQuery(graphql`
    query AllVariableText {
      allPricesCsv {
        nodes {
          name: Class
          Zone1
          India
          MXN
          Zone2
          Zone3
          Zone4
          Zone5
          AllEurosZ3
          CHF
          Canada
          BRL
          AUD
          GBP
          JPY
          SEK
          DKK
          NOK
          TWD
        }
      }
      allFormsCsv {
        nodes {
          name
          en
          de
          es
          fr
          it
          nl
          pt
          zh_Hans
          zh_Hant
        }
      }
      allLinksCsv {
        nodes {
          name
          en
          de
          es
          fr
          it
          nl
          pt
          zh_Hans
          zh_Hant
        }
      }
      allContentfulOptionSet(filter: {name: {eq: "globals"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { country,
    region,
    regionZone,
    developerRegions,
    language,
    freeTrialOverride,
    abTests } = useUserContext();
  const { search } = useLocation();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const formattedLanguage = language.replace('-', '_'); // CSV uses underscores instead of hyphens
  const pricingVariables = data.allPricesCsv.nodes;
  const formVariables = data.allFormsCsv.nodes;
  const linkVariables = data.allLinksCsv.nodes;
  let value = null;

  switch (type) {
    case 'phone-number': {
      const phoneNumbers = getPhoneNumbers(region, country);
      if (name === 'sales-support') {
        value = phoneNumbers;
      } else if (name === 'sales-only') {
        value = { salesPhone: phoneNumbers?.salesPhone };
      } else if (name === 'support-only') {
        value = { supportPhone: phoneNumbers?.supportPhone };
      }
      break;
    }
    case 'price': {
      const pricingVariable = pricingVariables.find((v) => v.name === name);
      if (pricingVariable !== undefined
        && pricingVariable[regionZone] !== undefined) {
        value = applyPricingFormat(language, country, pricingVariable[regionZone]);
      }
      break;
    }
    case 'form': {
      const formVariable = formVariables.find((v) => v.name === name);
      if (formVariable !== undefined
        && (formVariable[formattedLanguage] !== undefined || formVariable.en !== undefined)) {
        value = formVariable[formattedLanguage] ? formVariable[formattedLanguage] : formVariable.en;
      }
      break;
    }
    case 'link': {
      let tempName = name;
      const addParams = {};

      if (tempName === 'free-trial') {
        value = freeTrialOverride || options['globals.free_trial_default_link'];
        addParams.menutrial = 1;

        // Handle cases where the free trial override is itself a variable link
        const match = /{{link-([a-z0-9-_.]+)}}/gi.exec(value);
        if (match !== null && match.length > 1) {
          [, tempName] = match;
        }
      }

      // SOS links
      if (tempName === 'sos-app') {
        value = developerRegions.includes('eu') ? 'https://sos.splashtop.eu' : 'https://sos.splashtop.com';
      }

      // Enterprise Dynamic Link for Americas
      if (tempName === 'enterprise-trial-or-get-started') {
        value = developerRegions.includes('americas') ? 'https://redirect.splashtop.com/my/enterprise-trial/signup' : '/form/enterprise/contact-sales';
      } else if (tempName === 'enterprise-trial-or-learn-more') {
        value = developerRegions.includes('americas') ? 'https://redirect.splashtop.com/my/enterprise-trial/signup' : '/products/enterprise';
      }

      // SBA and SOS dynamic links default to the old my site forms
      if (tempName === 'my-sba-trial') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup';
      if (tempName === 'my-sba-trial-top-sba') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup?top=sba';
      if (tempName === 'my-sos-trial') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup';

      // Unified trial links
      if (tempName === 'trial') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup';
      if (tempName === 'access-trial') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup';
      if (tempName === 'support-trial') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup';

      // Unified links for homepage with new top=
      if (tempName === 'trial-top') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup?top=trial';
      if (tempName === 'access-trial-top') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup?top=access';
      if (tempName === 'support-trial-top') value = 'https://redirect.splashtop.com/my/splashtop-trial/signup?top=support';

      // Adjust links for SBA, SOS, and Free Trial
      const isDynamicLinkToAdjust = ['my-sba-trial', 'my-sba-trial-top-sba', 'my-sos-trial', 'trial', 'access-trial', 'support-trial', 'trial-top', 'access-trial-top', 'support-trial-top'].includes(tempName);
      const isUsingDefaultFreeTrialValue = value === options['globals.free_trial_default_link'];
      if (isDynamicLinkToAdjust || isUsingDefaultFreeTrialValue) {
        // Determine if new trial forms are supported
        const newTrialFormsSupported = (
          isBrowser
          && !/(ref=|platform=web-jp)/i.test(search)
          && !isVisitorMSIE()
          && !/(ja|ko)/i.test(getBrowserLanguage())
        );

        // If new trial form links are supported
        if (newTrialFormsSupported) {
          // Branch on the A/B test variation
          if (abTests.includes('future-ab')) {
            // Use this sample code to enable A/B testing of the trial form
            value = '/trial/choose?form=new-site-radio';
          } else {
            // Use the new site combined choose form by default
            value = '/trial/choose?form=new-site-radio';
          }

          // Change SOS links
          if (tempName === 'my-sos-trial') value = '/trial/remote-support?form=new-site-support';

          // Unified links for homepage with new top=
          if (tempName === 'trial') value = '/trial/choose?form=new-site-radio';
          if (tempName === 'access-trial') value = '/trial/remote-access?form=new-site-access';
          if (tempName === 'support-trial') value = '/trial/remote-support?form=new-site-support';

          // Unified links for homepage with new top=
          if (tempName === 'trial-top') value = '/trial/choose?form=new-site-radio&top=trial';
          if (tempName === 'access-trial-top') value = '/trial/remote-access?form=new-site-access&top=access';
          if (tempName === 'support-trial-top') value = '/trial/remote-support?form=new-site-support&top=support';

          // Add top=access if needed
          if (tempName === 'my-sba-trial-top-sba') value += '&top=access';
        }

        // Add abtest= tracking params
        let abtest = 'ineligible';
        if (newTrialFormsSupported) abtest = abTests.join(',') || 'none';
        value = mergeQueryParams(value, { abtest });
      }

      // Apply additional params
      if (Object.keys(addParams).length > 0) {
        value = mergeQueryParams(value, addParams);
      }

      if (tempName !== 'free-trial') {
        const linkVariable = linkVariables.find((v) => v.name === tempName);
        if (linkVariable !== undefined
          && (linkVariable[formattedLanguage] !== undefined || linkVariable.en !== undefined)) {
          value = linkVariable[formattedLanguage]
            ? linkVariable[formattedLanguage] : linkVariable.en;
        }
      }
      break;
    }
    case 'text':
      // Please update the documentation after adding one:
      // https://app.contentful.com/spaces/5i1m3im8l2b5/entries/4S3cW9MFu4UDirkbcpgIFn
      if (name === 'save-rs-perc') {
        value = '40%';
      } else if (name === '50-to-40-perc') {
        value = '40%';
      } else if (name === 'sba-volumn-perc-change') {
        value = '25%';
      } else if (name === 'sos-tenplus-teams') {
        value = 'SOS+10';
      } else if (name === 'trial-days') {
        // SBA trial in EU switched to 7 days for WWW-5176
        value = 7;
      } else if (name === 'general-trial-days') {
        value = 7;
      } else if (name === 'sba-trial-days') {
        value = 7;
      } else if (name === 'sos-trial-days') {
        value = ['eu', 'fr'].includes(region) ? '14' : '7';
      } else if (name === 'srs-trial-days') {
        value = ['eu', 'fr'].includes(region) ? '14' : '7';
      } else if (name === 'm360-trial-days') {
        value = ['eu', 'fr'].includes(region) ? '14' : '7';
      } else if (name === 'pricing-shown-usd') {
        const showPricesUsdText = !['eu', 'fr', 'canada', 'aud', 'sek', 'dkk'].includes(region);
        value = showPricesUsdText ? options['globals.prices_shown_usd'] : null;
      } else if (name === 'legal-translation-disclaimer') {
        value = language !== 'en' ? options['globals.legal_translation_disclaimer'] : null;
      } else if (name === 'empty') {
        // Used to test fields without outputting anything
        value = null;
      } else if (name === 'pricing-shown-exclude-taxes') {
        const showPricesTaxesText = !['default', 'aud', 'canada', 'emea', 'fr', 'gbp', 'jpy', 'yuan'].includes(region);
        value = showPricesTaxesText ? options['globals.prices_shown_exclude_taxes'] : null;
      } else if (name === 'enterprise-trial-or-get-started') {
        // Enterprise Dynamic Text for Americas
        value = developerRegions.includes('americas') ? options['globals.text_free_trial'] : options['globals.text_get_started'];
      } else if (name === 'enterprise-trial-or-learn-more') {
        value = developerRegions.includes('americas') ? options['globals.text_free_trial'] : options['globals.text_learn_more'];
      }
      break;
    default:
      break;
  }

  if (render !== undefined) {
    return render(value);
  }

  return value;
};

VariableText.propTypes = {
  type: PropTypes.oneOf(['phone-number', 'price', 'form', 'link', 'text']).isRequired,
  name: PropTypes.string,
  render: PropTypes.func
};

export default VariableText;
